// src/pages/PatientOnboardingPage.jsx
// Route: /patient/onboarding (shown once after first login — gated by hasCompletedOnboarding)
//
// Three-step flow:
//   Step 1 — Welcome: what Previu does + what to expect
//   Step 2 — Risk intake: family history, conditions, medications (Constitution §VIII)
//   Step 3 — First action: book lab test or upload existing report
//
// Constitution §VIII: "Risk stratification and symptom capture are clinical inputs
// that modify threshold sensitivity, pathway activation, and monitoring cadence
// across the entire system."
//
// Gap Report — PatientOnboardingPage.jsx
// ✅ Confirmed: 3-step structure, Constitution §VIII risk fields
// ⚠️ Assumptions: No Figma frame. Follows PatientLoginPage.jsx + PatientLayout design language.
// ❌ Missing Figma frames: All states. Fallback: design-system-consistent.
// 🔗 Existing: PatientLayout, usePatient(), useToast(), CSS design tokens
// 🚫 Deviations: None

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import HealthProfileForm from '../components/HealthProfileForm';
import Icon from '../components/Icon';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

const ONBOARDING_STORAGE_KEY  = 'previu_onboarding_complete';
const ONBOARDING_SKIPPED_KEY  = 'previu_onboarding_skipped';

// ── Risk intake fields (Constitution §VIII) ───────────────────────────────
// Family history + personal conditions + medications
// These are sent to the backend as profile updates and surfaced in triage context.

// ── Step components ────────────────────────────────────────────────────────

function WelcomeStep({ onNext }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{
        background: 'var(--brand-dark)',
        borderRadius: 16, padding: '28px 24px',
        textAlign: 'center',
      }}>
        <div className="empty-icon empty-icon--badge" aria-hidden="true">
          <Icon name="stethoscope" size={24} />
        </div>
        <h1 className="patient-onboarding__title" style={{
          fontFamily: 'var(--font-display)', fontSize: 24,
          margin: '0 0 10px', lineHeight: 1.3,
        }}>
          Welcome to Previu Health
        </h1>
        <p style={{
          fontSize: 14, color: 'rgba(255,255,255,0.75)',
          fontFamily: 'var(--font-body)', lineHeight: 1.7, margin: 0,
        }}>
          Your personal preventive health programme — designed for South Asians.
        </p>
      </div>

      {/* What Previu does */}
      {[
        { icon: '🧬', title: 'Understand your health', body: 'Upload your lab reports and get plain-language results — what they mean and what to do next.' },
        { icon: '📈', title: 'Track your health journey', body: 'Watch how your markers change over time. Catch trends before they become problems.' },
        { icon: '💬', title: 'Ask anything', body: 'Ask Previu can answer health questions, book appointments, and order lab tests — any time.' },
        { icon: '🏥', title: 'Care when you need it', body: 'Your health navigator and doctor are alerted when your results need attention.' },
      ].map(item => (
        <div key={item.title} style={{
          display: 'flex', gap: 14, alignItems: 'flex-start',
          padding: '16px', borderRadius: 12,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 24, flexShrink: 0 }}>{item.icon}</div>
          <div>
            <div style={{
              fontWeight: 600, fontSize: 14,
              color: 'var(--text-primary)', fontFamily: 'var(--font-body)',
              marginBottom: 4,
            }}>
              {item.title}
            </div>
            <div style={{
              fontSize: 13, color: 'var(--text-secondary)',
              fontFamily: 'var(--font-body)', lineHeight: 1.6,
            }}>
              {item.body}
            </div>
          </div>
        </div>
      ))}

      <button type="button" onClick={onNext} className="patient-onboarding__start" style={primaryBtn}>
        Get started →
      </button>
    </div>
  );
}

function RiskIntakeStep({ onNext, onBack }) {
  const { patient } = usePatient();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <div style={eyebrow}>Constitution §VIII — Risk Profile</div>
        <h2 style={heading}>Tell us about your health</h2>
        <p style={subtext}>
          This helps us personalise your health thresholds and catch issues earlier.
          You can update this any time in Settings.
        </p>
      </div>
      <HealthProfileForm
        patient={patient}
        saveLabel="Save and continue →"
        onSave={(data) => onNext(data)}
        onBack={onBack}
        onSkip={() => onNext({ conditions: [], familyHistory: [], medications: '', allergies: '', skipped: true })}
        compact
      />
    </div>
  );
}

function FirstActionStep({ onComplete }) {
  const navigate = useNavigate();

  function finish(path) {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    onComplete();
    navigate(path);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={eyebrow}>You're all set</div>
        <h2 style={heading}>Where would you like to start?</h2>
        <p style={subtext}>
          Choose one to get started. You can always change this from your dashboard.
        </p>
      </div>

      {[
        {
          icon: '🧪',
          title: 'Book a lab test',
          body: 'Order a Thyrocare comprehensive panel delivered to your home.',
          cta: 'Book now',
          path: '/patient/lab-tests/order',
          accent: true,
        },
        {
          icon: '📄',
          title: 'Upload an existing report',
          body: 'Already have a recent blood test? Upload it and get your results in minutes.',
          cta: 'Upload report',
          path: '/patient/upload',
        },
        {
          icon: '💬',
          title: 'Ask Previu',
          body: 'Have health questions? Start a conversation with your AI health assistant.',
          cta: 'Open Ask Previu',
          path: '/patient/chat',
        },
      ].map(item => (
        <div
          key={item.title}
          style={{
            border: `1.5px solid ${item.accent ? 'var(--brand-accent)' : 'var(--border)'}`,
            borderRadius: 12, padding: '18px 20px',
            background: item.accent ? 'rgba(184,132,58,0.06)' : 'var(--bg-surface)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{ fontSize: 24 }}>{item.icon}</div>
            <div>
              <div style={{
                fontWeight: 600, fontSize: 15, color: 'var(--text-primary)',
                fontFamily: 'var(--font-body)', marginBottom: 4,
              }}>
                {item.title}
              </div>
              <div style={{
                fontSize: 13, color: 'var(--text-secondary)',
                fontFamily: 'var(--font-body)', lineHeight: 1.6,
              }}>
                {item.body}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => finish(item.path)}
            style={{
              width: '100%', border: item.accent ? 'none' : '1.5px solid var(--border)',
              borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600,
              color: item.accent ? 'var(--text-on-brand-dark)' : 'var(--text-primary)',
              background: item.accent ? 'var(--brand-dark)' : 'var(--bg-surface)',
              cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}
          >
            {item.cta} →
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => { localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true'); onComplete(); navigate('/patient/today'); }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
          textAlign: 'center',
        }}
      >
        Go to my dashboard
      </button>
    </div>
  );
}

// ── Page container ────────────────────────────────────────────────────────

export default function PatientOnboardingPage() {
  const [step, setStep] = useState(1);
  const navigate        = useNavigate();
  const { patient }     = usePatient();
  const patientId       = patient?.id;

  // Stamp "onboarding has been shown" server-side, on render.
  //
  // Here rather than in the login flow, and on mount rather than on completion,
  // for two different reasons. On mount, because reaching this screen is the
  // event being recorded — a patient who skips has still seen it, and re-showing
  // it every login was the defect. In this component, because a login that
  // redirects here but never renders (a crash, a closed tab) should not consume
  // the patient's one showing.
  //
  // The old localStorage flag is still written below for backward compatibility
  // with anything reading it, but nothing routes on it any more: onboarding
  // state is a property of the patient, not of the browser.
  useEffect(() => {
    if (!patientId) return;
    fetch(`${API_BASE}/patients/${patientId}/onboarding-seen`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('previu_token')}`,
      },
    }).catch(() => {
      // Silent. Failing to stamp means the patient may see onboarding once
      // more, which is a far smaller problem than blocking the screen they are
      // standing on.
    });
  }, [patientId]);

  function handleComplete() {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    navigate('/patient/today');
  }

  const steps = [
    <WelcomeStep     key="welcome" onNext={() => setStep(2)} />,
    <RiskIntakeStep  key="risk"    onNext={() => setStep(3)} onBack={() => setStep(1)} />,
    <FirstActionStep key="action"  onComplete={handleComplete} />,
  ];

  return (
    <PatientAppShell breadcrumbs={[{ label: 'Getting started' }]}>
      <div className="patient-onboarding" style={{
        maxWidth: 560, margin: '0 auto',
        padding: '24px 0 60px',
      }}>
        {/* Step dots */}
        <div style={{
          display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 28,
        }}>
          {[1, 2, 3].map(s => (
            <div key={s}
              className={`patient-onboarding__dot${s === step ? ' patient-onboarding__dot--current' : ''}`}
              style={{
                width: s === step ? 24 : 8, height: 8,
                borderRadius: 4,
                transition: 'all 0.3s',
              }} />
          ))}
        </div>

        {steps[step - 1]}
      </div>
    </PatientAppShell>
  );
}

// ── Shared style helpers ──────────────────────────────────────────────────

const eyebrow = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: 'var(--brand-accent)',
  fontFamily: 'var(--font-body)', marginBottom: 6,
};

const heading = {
  fontFamily: 'var(--font-display)', fontSize: 22,
  color: 'var(--text-primary)', margin: '0 0 8px', lineHeight: 1.3,
};

const subtext = {
  fontSize: 14, color: 'var(--text-secondary)',
  fontFamily: 'var(--font-body)', lineHeight: 1.7, margin: 0,
};

// Colours come from .patient-onboarding__start in index.css, which flips them
// for the dark theme — an inline colour here would override that.
const primaryBtn = {
  flex: 1, border: 'none', borderRadius: 10,
  padding: '13px 18px', fontSize: 15, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'var(--font-body)',
};

const outlineBtn = {
  flex: 1, border: '1.5px solid var(--border)', borderRadius: 10,
  padding: '12px 18px', fontSize: 14, fontWeight: 600,
  color: 'var(--text-primary)', background: 'var(--bg-surface)',
  cursor: 'pointer', fontFamily: 'var(--font-body)',
};
